/**
 * 税率のパーセント入力 ⇔ API の小数文字列（0.1000 = 10%）の変換。
 *
 * 🔴 浮動小数点で割らない＝`5.5 / 100` は 2 進小数で誤差が出る。文字列のまま
 * 小数点を2桁ずらす（backend の Numeric(6,4) と同じ確定的な値になる）。
 * 粒度はパーセントで小数第2位まで（= 小数第4位の rate）。
 */

/** 入力として受けるパーセント（0〜100・小数第2位まで）。 */
const PERCENT_PATTERN = /^(\d{1,3})(?:\.(\d{1,2}))?$/;

/**
 * パーセント文字列 → API の rate 文字列（"10" → "0.1000"・"5.5" → "0.0550"）。
 * 受けられない入力（空・負・100 超・小数3桁以上・非数）は null。
 */
export function percentToRate(percent: string): string | null {
  const match = PERCENT_PATTERN.exec(percent.trim());
  if (!match) return null;
  const whole = Number(match[1]);
  if (whole > 100) return null;
  const fraction = (match[2] ?? "").padEnd(2, "0"); // "5" → "50"（= .50%）
  if (whole === 100 && fraction !== "00") return null;
  // 例: whole=10, fraction="00" → digits "1000" → rate 0.1000
  //     whole=5,  fraction="50" → digits "0550" → rate 0.0550
  const digits = `${String(whole).padStart(2, "0")}${fraction}`;
  if (whole === 100) return "1.0000";
  return `0.${digits.padStart(4, "0")}`;
}

/**
 * API の rate 文字列 → 表示用パーセント（"0.0550" → "5.5"・"0.1000" → "10"）。
 * 末尾の 0 は落とす（入力欄の初期値として自然な形にする）。
 */
export function rateToPercent(rate: string): string {
  const n = Number(rate);
  if (!Number.isFinite(n)) return "";
  // rate は小数第4位まで＝パーセントは第2位まで。toFixed(2) で誤差を吸収してから整える。
  const fixed = (n * 100).toFixed(2);
  return fixed.replace(/\.?0+$/, "") || "0";
}
